import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface RouteAccessValue {
  /** A rota atual respondeu com acesso negado. */
  denied: boolean;
  setDenied: (value: boolean) => void;
}

const RouteAccessContext = createContext<RouteAccessValue>({
  denied: false,
  setDenied: () => {},
});

/**
 * Sinaliza para o shell que o conteúdo da rota atual é uma negativa de acesso.
 *
 * O `AccessDenied` renderiza dentro do `<Outlet />`, abaixo do `Layout`, então
 * o Layout não tem como saber sozinho que a tela virou negativa. Sem esse
 * sinal, coisas que o shell dispara por rota continuam rodando por cima — o
 * caso concreto é a pesquisa de satisfação (CSAT), que aparecia perguntando o
 * que a pessoa achou de uma tela que ela não conseguiu abrir.
 */
export const RouteAccessProvider = ({ children }: { children: ReactNode }) => {
  const [denied, setDenied] = useState(false);
  const value = useMemo(() => ({ denied, setDenied }), [denied]);
  return (
    <RouteAccessContext.Provider value={value}>
      {children}
    </RouteAccessContext.Provider>
  );
};

/** Lido pelo shell. `false` fora do provider — nada quebra em teste isolado. */
export const useRouteAccessDenied = (): boolean =>
  useContext(RouteAccessContext).denied;

/**
 * Marca a rota como negada enquanto o componente estiver montado.
 *
 * Chamado pelo `AccessDenied`, então vale para qualquer negativa: a de rota
 * (`PermissionRoute`) e as de dentro de página (gestão de usuários).
 */
export const useReportAccessDenied = (): void => {
  const { setDenied } = useContext(RouteAccessContext);
  useEffect(() => {
    setDenied(true);
    return () => setDenied(false);
  }, [setDenied]);
};
